import {
    ArrowBack,
    CallOutline,
    CheckmarkCircleOutline,
    ChevronForward,
    GlobeOutline,
    LocationOutline,
    MailOutline,
    ShieldCheckmarkOutline,
    Star,
  } from "@raresail/react-ionicons";
  import { useNavigate, useParams } from "react-router-dom";
  import styled from "styled-components";
  import { InstitutionsData } from "../../../dump";
  import {
    currencyFormat,
    formatLargeNumber,
    numberFormat,
    stringify,
  } from "../../../utils/helpers";
  import { useTitle } from "../../../hooks/common/useTitle";
  import { EmptyView, Heading } from "../../elements/Elements";
  import {
    primary5,
    PRIMARY_COLOR,
    PRIMARY_LIGHT,
  } from "../../../constants/colors";
  import { alpha, IconButton } from "@mui/material";
  import { AnimateChildren } from "../../elements/Elements";
  
  export const InstitutionDetailPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const institution = InstitutionsData.find(
      (inst) => inst.id === Number(id)
    );
  
    useTitle(institution?.name || "Institution");
  
    if (!institution) {
      return (
        <div className="vh100Min flexCenter">
          <EmptyView source="institution" />
        </div>
      );
    }
  
    const {
      name,
      type,
      image,
      brandColor,
      tagline,
      description,
      about,
      mission,
      website,
      email,
      phone,
      rating,
      total_funds_managed,
      total_investors,
      location,
      established_year,
      regulated_by,
      products = [],
    } = institution;
  
    return (
      <PageWrapper className="flexColumn">
        <AnimateChildren>
          {/* ── Cover ── */}
          <CoverSection>
            <img src={image} alt={name} />
            <CoverOverlay />
            <TopBar>
              <IconButton
                onClick={() => navigate(-1)}
                sx={{
                  bgcolor: "rgba(255,255,255,0.92)",
                  "&:hover": { bgcolor: "#fff" },
                }}
                size="small"
              >
                <ArrowBack fontSize={18} />
              </IconButton>
            </TopBar>
          </CoverSection>
  
          {/* ── Header ── */}
          <Card className="flexColumn gap12">
            <div className="flexNullCenter gap15">
              <LogoImg src={image} alt={name} />
              <div className="flexGrow" style={{ minWidth: 0 }}>
                <div className="font18 bold textDark">{name}</div>
                <div
                  className="font12 capitalize mt3"
                  style={{ color: brandColor || PRIMARY_COLOR }}
                >
                  {stringify(type)}
                </div>
              </div>
              {rating && (
                <div className="flexNullCenter gap4 font13 semiBold textDark">
                  <Star fontSize={14} color="#f5a623" />
                  {rating}
                </div>
              )}
            </div>
  
            {tagline && (
              <div className="font13 grey" style={{ lineHeight: 1.5 }}>
                {tagline}
              </div>
            )}
  
            {regulated_by && (
              <div
                className="flexNullCenter gap6 font12"
                style={{ color: "#2e7d32" }}
              >
                <ShieldCheckmarkOutline fontSize={14} />
                {regulated_by}
              </div>
            )}
          </Card>
  
          {/* ── Key Figures ── */}
          <Card>
            <Heading className="font14 mb10">Key Figures</Heading>
            <StatsGrid>
              <StatBox>
                <div className="font16 bold textDark">
                  {formatLargeNumber(total_funds_managed)}
                </div>
                <div className="font11 grey">UGX Managed</div>
              </StatBox>
              <StatBox>
                <div className="font16 bold textDark">
                  {total_investors?.toLocaleString()}
                </div>
                <div className="font11 grey">Investors</div>
              </StatBox>
              <StatBox>
                <div className="font16 bold textDark">{established_year}</div>
                <div className="font11 grey">Established</div>
              </StatBox>
              <StatBox>
                <div className="font16 bold textDark">{products.length}</div>
                <div className="font11 grey">Products</div>
              </StatBox>
            </StatsGrid>
          </Card>
  
          {/* ── About ── */}
          <Card className="flexColumn gap12">
            <Heading className="font14">About</Heading>
            <div
              className="font13 textDark"
              style={{ lineHeight: 1.75, whiteSpace: "pre-line" }}
            >
              {about || description}
            </div>
  
            {mission && (
              <MissionBox className="r15 p15">
                <div className="font11 semiBold grey mb5">Our Mission</div>
                <div className="font13 textDark" style={{ lineHeight: 1.6 }}>
                  {mission}
                </div>
              </MissionBox>
            )}
          </Card>
  
          {/* ── Contact ── */}
          <Card className="flexColumn gap3">
            <Heading className="font14 mb8">Contact</Heading>
  
            {website && (
              <ContactRow
                onClick={() => window.open(website, "_blank")}
                className="pointer"
              >
                <IconDot color={brandColor || PRIMARY_COLOR}>
                  <GlobeOutline fontSize={14} />
                </IconDot>
                <div className="flexGrow font13 main">
                  {website.replace("https://", "").replace("www.", "")}
                </div>
                <ChevronForward fontSize={16} className="grey" />
              </ContactRow>
            )}
            {email && (
              <ContactRow>
                <IconDot color="#666">
                  <MailOutline fontSize={14} />
                </IconDot>
                <div className="font13 textDark">{email}</div>
              </ContactRow>
            )}
            {phone && (
              <ContactRow>
                <IconDot color="#666">
                  <CallOutline fontSize={14} />
                </IconDot>
                <div className="font13 textDark">{phone}</div>
              </ContactRow>
            )}
            {location && (
              <ContactRow>
                <IconDot color="#666">
                  <LocationOutline fontSize={14} />
                </IconDot>
                <div className="font13 textDark">{location}</div>
              </ContactRow>
            )}
          </Card>
  
          {/* ── Products ── */}
          <div style={{ padding: "0 15px" }}>
            <Heading className="font14">Products ({products.length})</Heading>
            <div className="font12 grey mt3 mb12">
              Investment products offered by {name}
            </div>
          </div>
  
          {products.map((product: any) => (
            <ProductCard key={product.id} data={product} brandColor={brandColor} />
          ))}
  
          {/* Disclaimer */}
          <div style={{ padding: "0 15px 30px" }}>
            <div
              className="font11 grey textCenter p15 r12"
              style={{ background: primary5, lineHeight: 1.5 }}
            >
              <b>NOTE:</b> Actual returns may vary depending on market conditions.
              Past performance does not guarantee future results.
            </div>
          </div>
        </AnimateChildren>
      </PageWrapper>
    );
  };
  
  // ─── Brand Logo ─────────────────────────────────────────────────
  
  // ─── Product Card with Calculator ───────────────────────────────
  const ProductCard = ({
    data,
    brandColor,
  }: {
    data: any;
    brandColor?: string;
  }) => {
    const {
      name,
      type,
      annual_rate,
      min_investment,
      return_range,
      term,
      risk,
      description,
      features = [],
      example,
    } = data;
  
    const accentColor = brandColor || PRIMARY_COLOR;
  
    return (
      <ProdWrapper className="whiteBg r15 hidden flexColumn">
        {/* Title bar */}
        <ProdHeader style={{ background: accentColor }}>
          <div className="font15 bold white">{name}</div>
          <div className="font11 white" style={{ opacity: 0.85 }}>
            {type} · {annual_rate}% p.a.
          </div>
        </ProdHeader>
  
        <div className="p18 flexColumn gap14">
          {/* Example Investment Calculator */}
          {example && (
            <CalcTable>
              <CalcHeader style={{ background: alpha(accentColor, 0.08) }}>
                <CalcCell className="font11 semiBold textDark">
                  Example Investment
                </CalcCell>
                <CalcCell className="font11 semiBold textDark">
                  Est. Monthly Interest
                </CalcCell>
                <CalcCell className="font11 semiBold textDark">
                  Est. Annual Interest
                </CalcCell>
                <CalcCell className="font11 semiBold textDark">
                  Value After 1 Year
                </CalcCell>
              </CalcHeader>
              <CalcRow>
                <CalcCell>
                  <div className="font10 grey">UGX</div>
                  <div className="font13 bold textDark">
                    {numberFormat(example.investment)}
                  </div>
                </CalcCell>
                <CalcCell>
                  <div className="font10 grey">UGX</div>
                  <div className="font13 bold textDark">
                    {numberFormat(example.monthly_interest)}
                  </div>
                </CalcCell>
                <CalcCell>
                  <div className="font10 grey">UGX</div>
                  <div className="font13 bold textDark">
                    {numberFormat(example.annual_interest)}
                  </div>
                </CalcCell>
                <CalcCell>
                  <div className="font10 grey">UGX</div>
                  <div className="font13 bold" style={{ color: "#2e7d32" }}>
                    {numberFormat(example.value_after_1yr)}
                  </div>
                </CalcCell>
              </CalcRow>
            </CalcTable>
          )}
  
          {/* Description */}
          <div className="font12 grey" style={{ lineHeight: 1.65 }}>
            {description}
          </div>
  
          {/* Details table */}
          <DetailsList>
            <DetailRow>
              <span className="grey">Minimum Investment</span>
              <span className="textDark semiBold">
                {currencyFormat(min_investment)}
              </span>
            </DetailRow>
            <DetailRow>
              <span className="grey">Expected Returns</span>
              <span className="semiBold" style={{ color: "#2e7d32" }}>
                {return_range} p.a.
              </span>
            </DetailRow>
            <DetailRow>
              <span className="grey">Term</span>
              <span className="textDark semiBold">{term}</span>
            </DetailRow>
            <DetailRow>
              <span className="grey">Risk Level</span>
              <span className="textDark semiBold">{risk}</span>
            </DetailRow>
          </DetailsList>
  
          {/* Features */}
          {features.length > 0 && (
            <div className="flexColumn gap8">
              <div className="font12 semiBold textDark">Key Features</div>
              {features.map((f: string, i: number) => (
                <div key={i} className="flexNullCenter gap8 font12 grey">
                  <CheckmarkCircleOutline fontSize={14} color="#2e7d32" />
                  {f}
                </div>
              ))}
            </div>
          )}
        </div>
      </ProdWrapper>
    );
  };
  
  // ─── Styles ─────────────────────────────────────────────────────
  const PageWrapper = styled.div`
    max-width: 600px;
  `;
  
  const CoverSection = styled.div`
    position: relative;
    height: 160px;
    overflow: hidden;
  
    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  `;
  
  const CoverOverlay = styled.div`
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 50%;
    background: linear-gradient(transparent, rgba(0, 0, 0, 0.2));
  `;
  
  const TopBar = styled.div`
    position: absolute;
    top: 10px;
    left: 10px;
  `;
  
  const LogoImg = styled.img`
    width: 52px;
    height: 52px;
    border-radius: 14px;
    object-fit: contain;
    background: ${primary5};
    padding: 4px;
    flex-shrink: 0;
  `;
  
  const Card = styled.div`
    margin: 12px 15px 0;
    padding: 18px;
    background: #fff;
    border-radius: 15px;
    border: 1px solid ${PRIMARY_LIGHT};
  `;
  
  const StatsGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 10px;
  `;
  
  const StatBox = styled.div`
    text-align: center;
    padding: 14px 10px;
    background: ${primary5};
    border-radius: 12px;
  `;
  
  const MissionBox = styled.div`
    background: ${primary5};
    border-left: 3px solid ${PRIMARY_COLOR};
  `;
  
  const ContactRow = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 0;
    border-bottom: 1px solid ${PRIMARY_LIGHT};
  
    &:last-child {
      border-bottom: none;
    }
  `;
  
  const IconDot = styled.div<{ color: string }>`
    width: 30px;
    height: 30px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    background: ${({ color }) => alpha(color, 0.08)};
    color: ${({ color }) => color};
    flex-shrink: 0;
  `;
  
  const ProdWrapper = styled.div`
    margin: 0 15px 12px;
    border: 1px solid ${PRIMARY_LIGHT};
    overflow: hidden;
  `;
  
  const ProdHeader = styled.div`
    padding: 16px 18px;
  `;
  
  // ─── Calculator Table ───────────────────────────────────────────
  const CalcTable = styled.div`
    border-radius: 12px;
    overflow: hidden;
    border: 1px solid ${PRIMARY_LIGHT};
  `;
  
  const CalcHeader = styled.div`
    display: grid;
    grid-template-columns: repeat(4, 1fr);
  `;
  
  const CalcRow = styled.div`
    display: grid;
    grid-template-columns: repeat(4, 1fr);
  `;
  
  const CalcCell = styled.div`
    padding: 10px 8px;
    text-align: center;
    border-right: 1px solid ${PRIMARY_LIGHT};
    line-height: 1.3;
  
    &:last-child {
      border-right: none;
    }
  `;
  
  // ─── Detail rows ────────────────────────────────────────────────
  const DetailsList = styled.div`
    border-radius: 12px;
    overflow: hidden;
    border: 1px solid ${PRIMARY_LIGHT};
  `;
  
  const DetailRow = styled.div`
    display: flex;
    justify-content: space-between;
    padding: 12px 14px;
    font-size: 12px;
    border-bottom: 1px solid ${PRIMARY_LIGHT};
  
    &:last-child {
      border-bottom: none;
    }
  
    &:nth-child(even) {
      background: ${primary5};
    }
  `;